import React, {useCallback, useEffect} from "react";
import {useParams} from "react-router-dom";
import {PublicTransitLayerEntityName} from "../../../../api/entities/replancity_PublicTransitLine";
import {useTypedSelector} from "../../../../redux/Hooks/storeSelectors";
import {selectMapLayerIdByEntityName} from "../../../../redux/selectors/selectors";
import EntityEditor from "../../../../components/Entities/EntityEditor";
import EntityManagerCleaner from "../../../../components/Entities/EntityManagerCleaner";
import {isErrorResponse, isUuidString} from "../../../../utils/utils";
import {flewToPoint} from "../../../../redux/map/map-reducer";
import {useDispatch} from "react-redux";
import {colorBehavior, getFeatureCenter} from "../../../../utils/mapUtils";
import {useMap} from "react-map-gl";
import {useMapFeatureLoader} from "../../../../hooks/entites/useMapFeatureLoader";


const StopEdit = () => {
    const stopEntityName = PublicTransitLayerEntityName.STOPS;
    const routesEntityName = PublicTransitLayerEntityName.ROUTES;
    const {stopId} = useParams<any>();
    const stopsLayerId = useTypedSelector((state) => selectMapLayerIdByEntityName(state, PublicTransitLayerEntityName.STOPS));
    const routesLayerId: string = useTypedSelector((state) => selectMapLayerIdByEntityName(state, PublicTransitLayerEntityName.ROUTES));
    const {loadFeature} = useMapFeatureLoader({entityName: stopEntityName});
    const dispatch = useDispatch();

    const {current: _map} = useMap();

    const highlightStop = useCallback((featureStateId: string) => {
        const map = _map?.getMap();

        map?.setPaintProperty(
            routesLayerId,
            'line-width',
            1
        );
        map?.setPaintProperty(
            routesLayerId,
            'line-opacity',
            0.7
        );
        map?.setPaintProperty(
            routesLayerId,
            'line-color',
            'grey'
        );

        map?.setPaintProperty(
            stopsLayerId,
            'circle-color',
            [
                'case',
                ['==', ['get', 'featureStateId'], featureStateId],
                "#db08e8",
                'grey'
            ]
        );

        map?.setPaintProperty(
            stopsLayerId,
            'circle-opacity',
            [
                'case',
                ['==', ['get', 'featureStateId'], featureStateId],
                1,
                0.1
            ]
        );
    }, [])

    useEffect(() => {
        return () => {
            try {
                const map = _map?.getMap();
                const lineWidth = map?.getPaintProperty(routesLayerId, 'line-width');
                const lineColor = map?.getPaintProperty(routesLayerId, 'line-color');

                map?.setPaintProperty(
                    routesLayerId,
                    'line-width',
                    lineWidth
                );
                map?.setPaintProperty(
                    routesLayerId,
                    'line-opacity',
                    1
                );
                map?.setPaintProperty(
                    routesLayerId,
                    'line-color',
                    lineColor
                );

                map?.setPaintProperty(
                    stopsLayerId,
                    'circle-opacity',
                    1
                );
                map?.setPaintProperty(
                    stopsLayerId,
                    'circle-color',
                    colorBehavior
                );

            } catch (e) {
                console.error("Replan. TransitStopsList: for the moment it's not clear how to fix this issue, therefore it's intentionally wrapped by try catch")
            }
        }
    }, [_map]);

    useEffect(() => {
        void (async function () {
            if (isUuidString(stopId)) {
                const {feature} = await loadFeature({
                    entityId: stopId!,
                    entityName: stopEntityName,
                    findFeatureParent: false
                });

                if (!isErrorResponse(feature)) {
                    const [lng, lat] = getFeatureCenter(feature);
                    if (lng && lat) {
                        dispatch(flewToPoint({lng, lat, zoom: 15}));
                    }

                    highlightStop(feature.properties.featureStateId.toString());
                }
            }
        }());
    }, [stopEntityName, routesEntityName, stopId, stopsLayerId])

    //TODO fix "fly to" and stop highlighting from useTransitStopLoader

    const getUrlOnSaveFn = useCallback((entity: any) => {
        if (!isUuidString(stopId)) {
            return `../`;
        }
    }, [stopId]);

    const onDeleteFn = useCallback(() => '../../', []);

    return (
        <EntityManagerCleaner entityName={stopEntityName}>
            <EntityEditor
                entityId={stopId!}
                entityName={stopEntityName}
                layerId={stopsLayerId}
                presetProperty={'publicTransitPreset'}
                getUrlOnSaveFn={getUrlOnSaveFn}
                getUrlOnDeleteFn={onDeleteFn}
                showDeleteBtn={isUuidString(stopId)}
            />
        </EntityManagerCleaner>
    )
}

export default StopEdit;